import { DocumentNode, gql, useMutation } from '@apollo/client';
import { Text } from '@chakra-ui/react';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';

import { ProfileImageCard_GroupFragment } from './graphql-types/ProfileImageCard_GroupFragment';
import {
  ProfileImageCard_UpdateGroup,
  ProfileImageCard_UpdateGroupVariables,
} from './graphql-types/ProfileImageCard_UpdateGroup';

import { GroupProfileImagePicker } from '../../../GroupProfileImagePicker';

const UPDATE_GROUP = gql`
  mutation ProfileImageCard_UpdateGroup($input: UpdateGroupInput!) {
    updateGroup(input: $input) {
      data {
        _id
        profileImage {
          _id
          imageProxy(input: { h: 96, w: 96, fit: "fill", fill: "solid" })
        }
      }
    }
  }
`;

type IProfileImageCardForm = {
  profileImageId?: string;
};

export const ProfileImageCard: React.FC<{
  group: ProfileImageCard_GroupFragment;
}> & { groupFragment: DocumentNode } = ({ group }) => {
  const [updateGroup] = useMutation<
    ProfileImageCard_UpdateGroup,
    ProfileImageCard_UpdateGroupVariables
  >(UPDATE_GROUP);

  const methods = useForm<IProfileImageCardForm>({
    defaultValues: {
      profileImageId: group.profileImage?._id ?? undefined,
    },
  });

  const profileImageId = methods.watch('profileImageId');

  React.useEffect(() => {
    const updateProfileImage = async (): Promise<void> => {
      try {
        await updateGroup({
          variables: {
            input: {
              id: group._id,
              data: { groupProfileImageId: profileImageId },
            },
          },
        });
      } catch (e) {
        logError(e);
      }
    };
    void updateProfileImage();
  }, [profileImageId, group._id, updateGroup]);

  return (
    <FormProvider {...methods}>
      <Card p="6" w="full">
        <Text textStyle="xlBodyStrong" mb="4">
          Profile Image
        </Text>
        <Controller
          control={methods.control}
          name="profileImageId"
          render={({ field }) => (
            <GroupProfileImagePicker
              {...field}
              nonDraftImageProxy={group.profileImage?.imageProxy ?? null}
            />
          )}
        />
      </Card>
    </FormProvider>
  );
};

ProfileImageCard.groupFragment = gql`
  fragment ProfileImageCard_GroupFragment on Group {
    _id
    profileImage {
      _id
      imageProxy(input: { h: 96, w: 96, fit: "fill", fill: "solid" })
    }
  }
`;
